// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import _ from 'lodash';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  getOperationalAlert,
  isDakotaModel,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
  permissiveStatusToIndicatorStatus,
  runningEnabledStatusToIndicatorStatus,
  runningStatusToIndicatorStatus,
  weatherModeStatusToTagType,
  weatherModeStatusToText,
} from '../../../../../../plugins/crusher';
import {
  convertPressure,
  convertTemperature,
} from '../../../../../../plugins/general';
import {
  LUBE_FLOW_RANGE,
  LUBE_PRESSURE_RANGE,
  LUBE_SUPPLY_DIFFERENTIAL_RANGE,
  LUBE_TEMPERATURE_RANGE,
  TEMPERATURE_RANGE,
} from '../../../../../../plugins/thresholds';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Types
import { CrusherModel } from '../../../../../../types/crusher';
import {
  MeasurementType,
  PressureType,
  TemperatureType,
} from '../../../../../../types/global';
import { Theme } from '../../../../../../types/theme';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Components
import { AlertLine } from '../../../../../../layout/icons';
import { AlertType } from '../../../../../../layout/elements/Alert';
import {
  Button,
  ButtonSize,
  ButtonType,
} from '../../../../../../layout/elements/Button';
import { Col } from '../../../../../../layout/elements/Col';
import { Drawer, DrawerSize } from '../../../../../../layout/elements/Drawer';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';

// Component
import { LubricationUnitProps } from './LubricationUnit.types';
import {
  LubePressureContainer,
  LubricationContainer,
  OverallStatusContainer,
  PermissiveItem,
  PermissiveListContainer,
  RunningStatusContainer,
  ThermometerContainer,
  WinterModeContainer,
  WinterModeStatus,
} from './LubricationUnit.style';

function LubricationUnit({
  className,
  isDrawerOpen,
  onCloseDrawer,
  unitData,
  edgeStatus,
  testId,
}: LubricationUnitProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { lubricationUnitDrawer } = theme.color.crusherDashboard;
  const {
    isXs,
    isLg,
    isXl,
    isXxl,
  } = useBreakpoint();

  const userPressureUnit: PressureType = getMeasurementUnit(MeasurementType.pressure);
  const userTemperatureUnit: TemperatureType = getMeasurementUnit(
    MeasurementType.temperature,
  );

  // Check if the model is Dakota (extract from operationalData)
  const isDakota: boolean = isDakotaModel(unitData?.crusherModelPLC);
  const isP500: boolean = unitData?.crusherModelPLC === CrusherModel.p500;
  const shouldShowLubeFlowScld1: boolean = isP500 && unitData?.lubeFlow1Enable === true;
  const shouldShowLubeFlowScld2: boolean = isP500 && unitData?.lubeFlow2Enable === true;
  const shouldShowLubeFlowScld: boolean = shouldShowLubeFlowScld1 || shouldShowLubeFlowScld2;

  const alertText: string | null = getOperationalAlert(
    edgeStatus?.edgeStatus,
    unitData?.timestamp,
    unitData?.readStatus,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  const renderLubePermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
      data-testid={`${testId}-lube-permissive-item`}
    >
      <StatusIndicator
        text={t(`view.crusher.lubrication.drawer.${permissiveName}`)}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  const renderHeaterPermissive = (
    permissiveName: string,
    permissive: boolean | null | undefined,
  ): JSX.Element => (
    <PermissiveItem
      status={permissiveStatusToIndicatorStatus(
        permissive,
        edgeStatus ? edgeStatus.edgeStatus : null,
        unitData?.timestamp,
      )}
      data-testid={`${testId}-heater-permissive-item`}
    >
      <StatusIndicator
        text={t(`view.crusher.lubrication.drawer.${permissiveName}`)}
        status={permissiveStatusToIndicatorStatus(
          permissive,
          edgeStatus ? edgeStatus.edgeStatus : null,
          unitData?.timestamp,
        )}
      />
    </PermissiveItem>
  );

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.crusher.lubrication.drawer.lubricationUnit')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      footer={false}
      size={DrawerSize.lg}
      alert={
        alertText
          ? {
            type: AlertType.warning,
            icon: <AlertLine />,
            description: alertText,
          }
          : false
      }
    >
      <LubricationContainer>
        <Row>
          <Col>
            <OverallStatusContainer data-testid={`${testId}-overall-status-container`}>
              <Paragraph
                color={lubricationUnitDrawer.overallStatusDescriptionTextColor}
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                testId={`${testId}-overall-status-description`}
              >
                {t('view.crusher.overallStatus')}
              </Paragraph>
              <Tag
                text={overallStatusToText(unitData?.lubricationOverallStatus)}
                type={
                  overallStatusToTagType(
                    unitData?.lubricationOverallStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </OverallStatusContainer>
          </Col>
        </Row>
        <Row>
          <Col xs={24} lg={12}>
            <RunningStatusContainer data-testid={`${testId}-running-status-container`}>
              <Paragraph
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
              >
                {t('view.crusher.lubrication.drawer.runningStatus')}
              </Paragraph>
              <Row gutter={12} style={{ rowGap: 24 }}>
                <Col>
                  <StatusIndicator
                    text={t('view.crusher.lubrication.drawer.lubePump')}
                    status={
                      runningStatusToIndicatorStatus(
                        unitData?.lubePumpRunningStatus,
                        edgeStatus ? edgeStatus.edgeStatus : null,
                        unitData?.timestamp,
                      )
                    }
                  />
                </Col>
                <Col>
                  <StatusIndicator
                    text={t('view.crusher.lubrication.drawer.lubeCooler')}
                    status={
                      runningStatusToIndicatorStatus(
                        unitData?.coolerFanStatus,
                        edgeStatus ? edgeStatus.edgeStatus : null,
                        unitData?.timestamp,
                      )
                    }
                  />
                </Col>
                <Col>
                  <StatusIndicator
                    text={t('view.crusher.lubrication.drawer.tankHeater')}
                    status={
                      runningEnabledStatusToIndicatorStatus(
                        unitData?.tankLubeHeaterStatus,
                        edgeStatus ? edgeStatus.edgeStatus : null,
                        unitData?.timestamp,
                      )
                    }
                  />
                </Col>
              </Row>
            </RunningStatusContainer>
          </Col>

          {/* Lube Pressure */}
          <Col xs={24} lg={12}>
            {!isDakota && (
              <LubePressureContainer data-testid={`${testId}-lube-pressure-container`}>
                <Speedometer
                  title={`${t('view.crusher.lubrication.drawer.lubePressure')}`}
                  unit={userPressureUnit}
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  value={
                    convertPressure(
                      unitData?.lubricationOilPressure,
                      userPressureUnit,
                      2,
                    ).value
                  }
                  decimalPlaces={
                    (() => {
                      // Show decimal places for values less than 1 (e.g., 0.15, 0.89, 0.00)
                      const convertedValue: number | null = convertPressure(
                        unitData?.lubricationOilPressure,
                        userPressureUnit,
                        2,
                      ).value;
                      return convertedValue !== null && convertedValue < 1 ? 2 : 0;
                    })()
                  }
                  minValue={
                    _.isNil(unitData?.lubricationOilPressure)
                      ? null
                      : LUBE_PRESSURE_RANGE(userPressureUnit).minValue
                  }
                  maxValue={
                    _.isNil(unitData?.lubricationOilPressure)
                      ? null
                      : LUBE_PRESSURE_RANGE(userPressureUnit).maxValue
                  }
                  thresholds={{
                    lowAlarm: convertPressure(
                      unitData?.spLubePressureLowAlarm,
                      userPressureUnit,
                      2,
                    ).value,
                    lowWarning: convertPressure(
                      unitData?.spLubePressureLowWarning,
                      userPressureUnit,
                      2,
                    ).value,
                  }}
                />
              </LubePressureContainer>
            )}
            {isDakota && unitData?.lubeFlowSwitchEnable && (
              <LubePressureContainer data-testid={`${testId}-lube-flow-container`}>
                <Speedometer
                  title={`${t('view.crusher.lubrication.drawer.lubeFlow')}`}
                  unit="GPM"
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  value={unitData?.lubeFlowScld}
                  minValue={
                    _.isNil(unitData?.lubeFlowScld)
                      ? null
                      : LUBE_FLOW_RANGE.minValue
                  }
                  maxValue={
                    _.isNil(unitData?.lubeFlowScld)
                      ? null
                      : LUBE_FLOW_RANGE.maxValue
                  }
                  thresholds={{}}
                />
              </LubePressureContainer>
            )}
          </Col>
        </Row>

        {/* Winter Mode */}
        <Row>
          <Col>
            <WinterModeContainer data-testid={`${testId}-winter-mode-container`}>
              <WinterModeStatus>
                <Paragraph
                  size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
                  weight={ParagraphWeight.bold}
                  color={lubricationUnitDrawer.winterModeTextColor}
                >
                  {t('view.crusher.lubrication.drawer.winterMode')}
                </Paragraph>
                <Tag
                  text={weatherModeStatusToText(unitData?.weatherModeStatus)}
                  type={
                    weatherModeStatusToTagType(
                      unitData?.weatherModeStatus,
                      edgeStatus ? edgeStatus.edgeStatus : null,
                      unitData?.timestamp,
                    )
                  }
                />
              </WinterModeStatus>
              <Button
                disabled
                type={ButtonType.secondary}
                size={ButtonSize.xs}
                block={false}
                onClick={() => {}}
                testId={`${testId}-winter-mode-button`}
              >
                {
                  isXs
                    ? t('view.crusher.lubrication.drawer.accessWinterModeMobile')
                    : t('view.crusher.lubrication.drawer.accessWinterMode')
                }
              </Button>
            </WinterModeContainer>
          </Col>
        </Row>

        {/* Thermometers */}
        <Row style={{ width: 'calc(100% + 1px)' }}>
          <ThermometerContainer
            xs={12}
            lg={unitData?.lubeSupplyTempEnable ? 6 : 12}
            divider
            testId={`${testId}-thermometer-return-temp-container`}
          >
            <Thermometer
              running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
              thresholds={{
                lowAlarm: convertTemperature(
                  unitData?.spLubeReturnTempLowAlarm,
                  userTemperatureUnit,
                  1,
                ).value,
                lowWarning: convertTemperature(
                  unitData?.spLubeReturnTempLowWarning,
                  userTemperatureUnit,
                  1,
                ).value,
                highWarning: convertTemperature(
                  unitData?.spLubeReturnTempHighWarn,
                  userTemperatureUnit,
                  1,
                ).value,
                highAlarm: convertTemperature(
                  unitData?.spLubeReturnTempStopFeed,
                  userTemperatureUnit,
                  1,
                ).value,
              }}
              minValue={
                  _.isNil(unitData?.lubricationReturnTemperature)
                    ? null
                    : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                }
              maxValue={
                  _.isNil(unitData?.lubricationReturnTemperature)
                    ? null
                    : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                }
              value={
                  convertTemperature(
                    unitData?.lubricationReturnTemperature,
                    userTemperatureUnit,
                    1,
                  ).value
                }
              unit={userTemperatureUnit}
              title={`${t('view.crusher.lubrication.drawer.returnTemp')}`}
            />
          </ThermometerContainer>
          <ThermometerContainer
            divider={isLg || isXl || isXxl}
            xs={12}
            lg={unitData?.lubeSupplyTempEnable ? 6 : 12}
            testId={`${testId}-thermometer-tank-temp-container`}
          >
            <Thermometer
              running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
              thresholds={{
                lowAlarm: convertTemperature(
                  unitData?.spLubeTankTempLowAlarm,
                  userTemperatureUnit,
                  1,
                ).value,
                lowWarning: convertTemperature(
                  unitData?.spLubeTankTempLowWarning,
                  userTemperatureUnit,
                  1,
                ).value,
                highWarning: convertTemperature(
                  unitData?.spLubeTankTempHighWarning,
                  userTemperatureUnit,
                  1,
                ).value,
                highAlarm: convertTemperature(
                  unitData?.spLubeTankTempHighAlarm,
                  userTemperatureUnit,
                  1,
                ).value,
              }}
              minValue={
                _.isNil(unitData?.lubricationTankTemperature)
                  ? null
                  : TEMPERATURE_RANGE(userTemperatureUnit).minValue
              }
              maxValue={
                _.isNil(unitData?.lubricationTankTemperature)
                  ? null
                  : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
              }
              value={
                convertTemperature(
                  unitData?.lubricationTankTemperature,
                  userTemperatureUnit,
                  1,
                ).value
              }
              unit={userTemperatureUnit}
              title={`${t('view.crusher.lubrication.drawer.tankTemp')}`}
            />
          </ThermometerContainer>
          <ThermometerContainer
            xs={unitData?.lubeSupplyTempEnable ? 12 : 0}
            lg={unitData?.lubeSupplyTempEnable ? 6 : 0}
            divider
            testId={`${testId}-thermometer-supply-temp-container`}
          >
            <Thermometer
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
              thresholds={{
                lowWarning: convertTemperature(
                  unitData?.spLubeSupplyTempLowWarn,
                  userTemperatureUnit,
                  1,
                ).value,
              }}
              minValue={
                _.isNil(unitData?.lubricationSupplyTemperature)
                  ? null
                  : LUBE_TEMPERATURE_RANGE(userTemperatureUnit).minValue
              }
              maxValue={
                _.isNil(unitData?.lubricationSupplyTemperature)
                  ? null
                  : LUBE_TEMPERATURE_RANGE(userTemperatureUnit).maxValue
              }
              value={
                convertTemperature(
                  unitData?.lubricationSupplyTemperature,
                  userTemperatureUnit,
                  1,
                ).value
              }
              unit={userTemperatureUnit}
              title={`${t('view.crusher.lubrication.drawer.supplyTemp')}`}
            />
          </ThermometerContainer>
          <ThermometerContainer
            xs={unitData?.lubeSupplyTempEnable ? 12 : 0}
            lg={unitData?.lubeSupplyTempEnable ? 6 : 0}
            testId={`${testId}-thermometer-differential-container`}
          >
            <Thermometer
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
              minValue={
                _.isNil(unitData?.lubeSupplyDifferential)
                  ? null
                  : LUBE_SUPPLY_DIFFERENTIAL_RANGE(userTemperatureUnit).minValue
              }
              maxValue={
                _.isNil(unitData?.lubeSupplyDifferential)
                  ? null
                  : LUBE_SUPPLY_DIFFERENTIAL_RANGE(userTemperatureUnit).maxValue
              }
              value={
                _.isNil(unitData?.lubeSupplyDifferential)
                  ? null
                  : Number(convertTemperature(
                    unitData?.lubeSupplyDifferential,
                    userTemperatureUnit,
                    1,
                  ).value)
              }
              thresholds={{
                highWarning: convertTemperature(
                  unitData?.spLubeSupplyReturnTempDiffAlarm,
                  userTemperatureUnit,
                  1,
                ).value,
              }}
              unit={userTemperatureUnit}
              title={`${t('view.crusher.lubrication.drawer.lubeSupplyDifferential')}`}
            />
          </ThermometerContainer>
        </Row>

        {shouldShowLubeFlowScld && (
          <Row style={{ width: 'calc(100% + 1px)' }} data-testid={`${testId}-lube-flow-scld-container`}>
            {shouldShowLubeFlowScld1 && (
              <ThermometerContainer
                xs={12}
                lg={12}
                divider={shouldShowLubeFlowScld2}
                testId={`${testId}-lube-flow-scld1-container`}
              >
                <Speedometer
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  minValue={
                    _.isNil(unitData?.lubeFlowScld1)
                      ? null
                      : LUBE_FLOW_RANGE.minValue
                  }
                  maxValue={
                    _.isNil(unitData?.lubeFlowScld1)
                      ? null
                      : LUBE_FLOW_RANGE.maxValue
                  }
                  value={unitData?.lubeFlowScld1}
                  unit="L/min"
                  thresholds={{}}
                  title={`${t('view.crusher.lubrication.drawer.lubeFlowScld1')}`}
                />
              </ThermometerContainer>
            )}
            {shouldShowLubeFlowScld2 && (
              <ThermometerContainer
                xs={12}
                lg={12}
                testId={`${testId}-lube-flow-scld2-container`}
              >
                <Speedometer
                  running={
                    isEquipmentRunning(
                      edgeStatus,
                      unitData?.timestamp,
                      DEVICE_COMMUNICATION_THRESHOLD,
                    )
                  }
                  minValue={
                    _.isNil(unitData?.lubeFlowScld2)
                      ? null
                      : LUBE_FLOW_RANGE.minValue
                  }
                  maxValue={
                    _.isNil(unitData?.lubeFlowScld2)
                      ? null
                      : LUBE_FLOW_RANGE.maxValue
                  }
                  value={unitData?.lubeFlowScld2}
                  unit="L/min"
                  thresholds={{}}
                  title={`${t('view.crusher.lubrication.drawer.lubeFlowScld2')}`}
                />
              </ThermometerContainer>
            )}
          </Row>
        )}

        {/* Lube Permissives */}
        <Row>
          <Col xs={24} lg={12}>
            <PermissiveListContainer
              borderBottom={!isLg && !isXl && !isXxl}
              borderRight={isLg || isXl || isXxl}
              data-testid={`${testId}-lube-permissive-container`}
            >
              <StatusIndicator
                text={t('view.crusher.lubrication.drawer.lubePermissive')}
                status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.lubePermissive,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                size={StatusIndicatorSize.lg}
                testId={`${testId}-lube-permissive-indicator`}
              />
              {renderLubePermissive('lubePump1StarterFault', unitData?.lubePump1StarterFault)}
              {renderLubePermissive('lubeOilPressure', unitData?.lubeOilPressure)}
              {renderLubePermissive('heaterEnabled', unitData?.lubeTankHeaterEnable)}
              {renderLubePermissive('lubeTankOilLevel', unitData?.lubeTankOilLevel)}
              {renderLubePermissive('tankTempTooLowToRun', unitData?.tankTempTooLowToRun)}
              {renderLubePermissive('tankTempTooLowToStart', unitData?.tankTempTooLowToStart)}
            </PermissiveListContainer>
          </Col>

          {/* Lube Tank Heater Permissives */}
          <Col xs={24} lg={12}>
            <PermissiveListContainer data-testid={`${testId}-heater-permissive-container`}>
              <StatusIndicator
                text={t('view.crusher.lubrication.drawer.lubeTankHeater')}
                status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.lubeHeaterPermissive,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                size={StatusIndicatorSize.lg}
                testId={`${testId}-heater-permissive-indicator`}
              />
              {renderHeaterPermissive(
                'lubeHeaterStarterFault',
                unitData?.lubeHeaterStarterFault,
              )}
              {renderHeaterPermissive('lubeTankLevel', unitData?.lubeTankLevel)}
              {renderHeaterPermissive(
                'lubeTankTempTransducerFault',
                unitData?.lubeTankTempTransducerFault,
              )}
            </PermissiveListContainer>
          </Col>
        </Row>
      </LubricationContainer>
    </Drawer>
  );
}

export { LubricationUnit };
